import { _ } from "../../core/polyglot/polyglot.js";
import { download } from "../../core/helpers/uri-util.js";
import { homeIcon } from "../../core/elements/icons/index.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import accountingDocument from "../shared/accountingDocument.js";
import reportDocumentsPanelWidget from "../reports/reportDocumentsPanelWidget.js";
import ReportService from "./ReportService.js";
import ReportTemplate from "../../models/reports/templates/ReportTemplate.js";
import session from "../../core/session.js";
import SwitchButton from "../../core/widgets/SwitchButton/SwitchButton.js";

/**
 * Todo: Add JSDoc comment
 */
function customizedReportsDocument(spec, my) {
	my ||= {};
	spec ||= {};

	const that = accountingDocument(spec, my);

	my.templateRepository = makeMonitorRepository({
		route: "/Shared/ReportTemplate/Company/#companyId/?id",
		model: ReportTemplate,
		defaultFilters: {
			companyId: session.getCompany().getLegacyId(),
		},
		antiForgeryToken: session.getAntiForgeryToken(),
	});

	my.reportService = new ReportService({
		company: session.getCompany(),
		period: session.getPeriod(),
		chartOfAccountsId: session.getCurrentChartOfAccountsId(),
	});

	session.onPeriodChange((period) => {
		my.reportService.setPeriod(period);
	});

	that.documentId = "customized-reports";

	that.hasPeriodPicker = function () {
		return true;
	};

	that.hasActionsMenu = function () {
		return session
			.getCurrentUser()
			.canManageReportTemplates(session.getCompany());
	};

	that.actions = function () {
		return [my.editActionsRenderer(), my.visibilityRenderer()];
	};

	that.title = (html) => {
		if (my.template) {
			html.render(my.template.getTitle());
		} else {
			html.render(_("Customized reports"));
		}
	};

	that.subtitle = (html) => {
		if (my.template) {
			html.render(my.template.getDescription());
		} else {
			html.render(_("Reports tailor-made for your company"));
		}
	};

	that.help = function () {
		return [
			{
				title: _("Customized reports"),
				content: _(
					"All available {templateName} reports are displayed for easy access.",
					{
						templateName: my.template
							? `"${my.template.getTitle()}"`
							: "",
					},
				),
			},
		];
	};

	that.show = async function (templateId) {
		that.showUpdating();

		my.template = await my.templateRepository.get({
			filter: { id: templateId },
		});

		if (my.template) {
			that.showContent();

			my.updateActions();
		} else {
			that.showEmpty();
		}

		my.updateActions();
	};

	my.renderContentOn = function (html) {
		my.renderBreadcrumbsOn(html);

		html.render(
			my.section({
				id: "customized-reports",
				content: my.renderReportsOn,
			}),
		);
	};

	my.renderBreadcrumbsOn = function (html) {
		html.div(
			{ klass: "breadcrumbs" },
			html.a({ href: my.linkTo("customized-reports") }, homeIcon),
			html.span({ class: "separator" }, "/"),
			html.span(my.template.getTitle()),
		);
	};

	my.renderReportsOn = function (html) {
		html.render(
			reportDocumentsPanelWidget({
				type: `report-viewer/${my.template.getId()}`,
				currentPeriod: session.getPeriod(),
			}),
		);
	};

	my.editActionsRenderer = function () {
		if (!my.template) {
			return () => {};
		}

		return my.rightMenuSection(_("Actions"), (html) => {
			html.button(
				{
					klass: "btn btn-block btn-default",
					click: () => my.redirectToReportEditor(),
					"data-test": "edit",
				},
				_("Edit"),
			);

			html.button(
				{
					klass: "btn btn-block btn-default",
					click: () => my.exportTemplate(),
					"data-test": "export",
				},
				_("Export"),
			);

			html.button(
				{
					klass: "btn btn-block btn-warning",
					click: () => my.deleteTemplate(),
					"data-test": "delete",
				},
				_("Delete"),
			);
		});
	};

	my.visibilityRenderer = function () {
		if (!my.template) {
			return () => {};
		}

		const button = new SwitchButton({
			dataTestName: "visible",
			state: my.template.isVisibleToAllUsers(),
		});

		button.stateChanged.register((state) => {
			my.template.setVisibleToAllUsers(state);

			my.reportService.saveReportTemplate({
				reportTemplate: my.template,
			});
		});

		return my.rightMenuSection("", (html) => {
			html.div(
				{ klass: "right-menu-block" },
				html.span(
					{ klass: "switch-label" },
					_("Visible to client users"),
				),
				button,
			);
		});
	};

	my.redirectToReportEditor = function () {
		my.redirectTo(`report-editor/${my.template.getId()}`);
	};

	my.exportTemplate = function () {
		const data = JSON.stringify(my.template.serialize());

		download({
			data,
			mimetype: "text/json;charset=utf-8;",
			filename: `${my.template.getTitleString()}.txt`,
		});
	};

	my.deleteTemplate = function () {
		my.confirmDeletion({
			name: my.template.getTitle(),
			callback: () =>
				my.reportService.removeReportTemplate({
					reportTemplate: my.template,
					callback: () => my.redirectTo("customized-reports"),
				}),
		});
	};

	that.showUpdating();

	return that;
}

export default customizedReportsDocument;
